import { createTheme, ThemeOptions } from '@mui/material';
import { green, grey, orange, red } from '@mui/material/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LinkBehavior } from './layout/LinkBehavior.tsx';

export const branding = {
  fontFamily: 'Lato, sans-serif',
  paletteOptions: {
    primary: {
      main: '#2cd5c4',
      light: '#2cd5c4',
      dark: '#005151',
      contrastText: '#ffffff',
    },
    menuColor: '#E8F4F4',
    grey: {
      50: '#FBFBFB',
      100: '#F3F3F3',
      200: '#E1E1E2',
      300: '#D4D5D5',
      400: '#AEAEAF',
      500: '#979797',
      600: '#6D6E70',
      700: '#4F4F50',
      900: '#101828',
    },
  },
};

/**
 * If adding new mixins or variables to this file remember to update theme.d.ts definitions too
 */

// theme is split into two parts to allow second part to reference variables declared in first
const baseTheme = createTheme({
  typography: {
    // text size reset
    fontSize: 24,
    fontFamily: 'ui-sans-serif, system-ui, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
    button: {
      textTransform: 'none',
    },
    h1: {
      fontSize: '4.8rem',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          position: 'initial',
          boxShadow: 'none',
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
        LinkComponent: LinkBehavior,
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      },
      styleOverrides: {
        root: {
          textDecoration: 'none',
          ':hover': {
            color: 'inherit',
            textDecoration: 'none',
          },
        },
      },
    },
  },
  palette: {
    secondary: {
      main: branding.paletteOptions.grey[600],
      light: branding.paletteOptions.grey[300],
      dark: grey[700],
    },
    text: {
      primary: '#000000',
    },
    background: {
      default: grey[100],
    },
    success: {
      main: green[500],
      light: green[50],
      dark: green[700],
    },
    warning: {
      main: orange[500],
      light: orange[50],
      dark: orange[700],
    },
    error: {
      main: red[500],
      light: red[50],
      dark: red[700],
    },
    info: {
      main: '#0288D1',
      light: '#E5F6FD',
      dark: '#014361',
    },
    shadows: {
      light: '#10182808',
      dark: '#10182814',
    },
    ...branding.paletteOptions,
  },
});

const borderRadius = baseTheme.spacing(1);

// TODO: Hoist the Paper style here to all MuiPaper
const paperStyles = {
  borderRadius,
  boxShadow: `0px 2px 4px -2px ${baseTheme.palette.shadows.light}, 0px 4px 8px -2px ${baseTheme.palette.shadows.dark}`,
  border: `1px solid ${baseTheme.palette.grey[200]}`,
  marginTop: baseTheme.spacing(0.5),
};

const getAlertCloseIconStyle = (severity: 'info' | 'error' | 'success' | 'warning') => ({
  width: baseTheme.spacing(2),
  height: baseTheme.spacing(2),
  fill: baseTheme.palette[severity].main,
});

const getAlertStyles = (severity: 'info' | 'error' | 'success' | 'warning') => ({
  backgroundColor: baseTheme.palette[severity].light,
  borderColor: baseTheme.palette[severity].main,
  color: baseTheme.palette[severity].dark,
  '& .MuiAlert-action': {
    '& .MuiButtonBase-root': {
      ':hover': {
        backgroundColor: 'unset',
      },
    },
    '& .MuiButtonBase-root .MuiSvgIcon-root': getAlertCloseIconStyle(severity),
  },
});

const getButtonStyles = (color: 'primary' | 'secondary', variant: 'text' | 'outlined' | 'contained') => {
  switch (variant) {
    case 'outlined':
      return {
        borderColor: '#00968F',
        color: '#00968F',
        ':hover': {
          backgroundColor: '#E8F4F4',
        },
      };
    case 'contained':
      return {
        backgroundColor: '#007F7A',
        color: baseTheme.palette[color].contrastText,
        ':hover': {
          backgroundColor: '#005151',
        },
      };
    default:
      return {};
  }
};

export const theme = createTheme(baseTheme, {
  mixins: {
    // App toolbar height should be controlled from here so useAppBarHeight can pick it up
    toolbar: {
      minHeight: 80,
      '@media (min-width:600px)': { minHeight: 80 },
      '@media (min-width:0px) and (orientation: landscape)': { minHeight: 80 },
    },
    modalStyle: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      maxHeight: '75vh',
      display: 'flex',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius,
      border: 'none',
      outline: 'none',
      p: 3,
    },
    activeButton: {
      border: `2px solid ${baseTheme.palette.primary.main} !important`,
      boxShadow: `0px 0px 0px 4px ${baseTheme.palette.primary.light}`,
    },
    roundedIconButton: {
      borderWidth: '1px',
      borderRadius: baseTheme.spacing(1),
      borderStyle: 'solid',
    },
  },
  components: {
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 4,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        rounded: {
          borderRadius,
        },
      },
    },
    MuiPaper: { styleOverrides: { root: paperStyles } },
    MuiFormControl: {
      styleOverrides: {
        root: {
          gap: 2,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          color: baseTheme.palette.grey[700],
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ExpandMoreIcon,
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <ExpandMoreIcon />,
      },
    },
    MuiPickersCalendarHeader: {
      defaultProps: {
        slots: {
          switchViewIcon: ExpandMoreIcon,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '& .MuiMenuItem-root + .MuiDivider-root': {
            marginTop: 0,
            marginBottom: 0,
          },
        },
      },
    },
    MuiDivider: {
      defaultProps: {
        variant: 'middle',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&, input, fieldset': {
            borderRadius,
            borderColor: baseTheme.palette.grey[400],
          },
          '&:not(:is(:focus, .Mui-disabled)):hover fieldset.MuiOutlinedInput-notchedOutline': {
            borderColor: baseTheme.palette.primary.dark,
            borderWidth: '2px',
            textDecoration: 'none',
          },
          '&:is(.Mui-focused, :focus) fieldset.MuiOutlinedInput-notchedOutline': {
            borderColor: `${baseTheme.palette.primary.main} !important`,
            boxShadow: `0px 0px 0px 4px ${baseTheme.palette.primary.dark}7A`,
          },
          '&:is(.Mui-disabled)': {
            backgroundColor: `${baseTheme.palette.grey[100]}`,
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          boxShadow: `0px 1px 2px 0px ${baseTheme.palette.grey[700]}, 0px 1px 3px 0px ${baseTheme.palette.grey[400]}`,
          borderRadius,
          borderCollapse: 'separate',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: baseTheme.palette.grey[100],
          fontWeight: 500,
        },
      },
    },
    MuiMultiSectionDigitalClock: {
      styleOverrides: {
        root: {
          '& .MuiButtonBase-root': {
            borderRadius,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius,
          // Used to style alert content when it's an HTML string
          '& .MuiAlert-message *:not([class*="MuiButtonBase-root"])': {
            color: 'inherit',
            fontSize: baseTheme.spacing(1.75),
            lineHeight: baseTheme.spacing(2.5),
          },
        },
        message: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        },
        outlinedInfo: getAlertStyles('info'),
        outlinedError: getAlertStyles('error'),
        outlinedSuccess: getAlertStyles('success'),
        outlinedWarning: getAlertStyles('warning'),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: 'none',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 'unset',
          padding: 0,
          '&.Mui-expanded': {
            minHeight: 'unset',
            marginBottom: baseTheme.spacing(1.5),
          },
        },
        content: {
          margin: 0,
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
          paddingBottom: baseTheme.spacing(1.5),
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: baseTheme.spacing(2),
          ':last-child': {
            paddingBottom: baseTheme.spacing(2),
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: `${baseTheme.spacing(1.5)} ${baseTheme.spacing(1)}`,
          borderRadius: baseTheme.spacing(0.75),
          '& .MuiTypography-root': {
            fontSize: baseTheme.spacing(1.75),
            fontWeight: 500,
            lineHeight: baseTheme.spacing(2.5),
          },
          // Used for buttons that render as a link
          '&.MuiButton-containedPrimary[href]:hover': {
            color: baseTheme.palette.primary.contrastText,
          },
          '&.Mui-disabled': {
            backgroundColor: baseTheme.palette.grey[100],
            color: baseTheme.palette.grey[500],
            border: `1px solid ${baseTheme.palette.grey[300]}`,
          },
        },
        sizeLarge: {
          padding: `${baseTheme.spacing(1)} ${baseTheme.spacing(4)}`,
          height: baseTheme.spacing(5.5),
        },
        sizeMedium: {
          padding: `${baseTheme.spacing(1)} ${baseTheme.spacing(2)}`,
          height: baseTheme.spacing(5),
        },
        sizeSmall: {
          padding: `${baseTheme.spacing(1)} ${baseTheme.spacing(1.5)}`,
          height: baseTheme.spacing(4.5),
        },
        containedPrimary: getButtonStyles('primary', 'contained'),
        containedSecondary: getButtonStyles('secondary', 'contained'),
        outlinedPrimary: getButtonStyles('primary', 'outlined'),
        outlinedSecondary: getButtonStyles('secondary', 'outlined'),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.MuiIconButton-colorSecondary': {
            borderColor: baseTheme.palette.grey[400],
          },
          '&.Mui-disabled': {
            backgroundColor: baseTheme.palette.grey[100],
            color: baseTheme.palette.grey[500],
            border: `1px solid ${baseTheme.palette.grey[300]}`,
          },
          '&.openDatePickerIconButton.Mui-disabled': {
            border: 'none',
          },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        rounded: {
          borderRadius,
        },
      },
    },
  },
} as ThemeOptions);
